// Точна класифікація запчастин за назвою/описом у межах одного розділу
// (parts-cars → parts-engine тощо). Використовується скрапером OLX
// (розділ «Автозапчастини» не має URL-підкатегорій) та імпортом
// власного товару з Ai-Business.
//
// Словники — порт production-даних каталогів perfect-parts.pp.ua
// (pp_categories.py) та razborkaleaf.pp.ua (rl_categories.py),
// об'єднані в консолідовану таксономію і доповнені українськими
// ключами. Алгоритм: виграє найдовше совпадіння ключа (тоді
// «радіатор пічки» → Охолодження, а не просто «радіатор»).
import { findCategoryById } from './categories.js';
function normalize(text) {
    return text
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/ї/g, 'і')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Ключові слова підкатегорій. Окремі карти для різних областей:
 * 'parts-cars' — основна; 'parts-wheels'/'parts-accessories'/
 * 'parts-trucks' — свої (короткі ключі типу «диск» безпечні лише
 * всередині своєї області).
 */
const scopedKeywords = {
    'parts-cars': {
        // ЕВ: батарея, зарядка (pered Двигуном/Електрикою — специфічніші ключі)
        'parts-ev': [
            'ввб',
            'батарея',
            'блок батарей',
            'модуль батарей',
            'батарейн',
            'hev battery',
            'li-ion',
            'літій-іонн',
            'литиев',
            'зарядн',
            'зарядка',
            'порт заряд',
            'зарядний пристрій',
            'зарядное устройство',
            'перетворювач',
            'преобразователь',
            'dc-dc',
            'onboard',
            'obc',
            'evse',
            'type2',
            'вилка заряд',
            'інвертор',
            'инвертор',
            'електромотор',
            'электромотор',
            'редуктор електромотора',
        ],
        'parts-engine': [
            'двигун',
            'двигатель',
            'двигуна',
            'мотор',
            'генератор',
            'стартер',
            'турбокомпресор',
            'турбокомпрессор',
            'турбина',
            'турбин',
            'колінвал',
            'коленвал',
            'розподілвал',
            'распредвал',
            'поршень',
            'поршнев',
            'головка блок',
            'головка',
            'блок циліндр',
            'блок двигун',
            'прокладк',
            'ланцюг',
            'цепь',
            'ремінь генератора',
            'ремень генератора',
            'ремінь грм',
            'ремень грм',
            'ремінь',
            'ремень',
            'ролик',
            'натяжитель',
            'натяжні',
            'дмрв',
            'дмрв',
            'дросель',
            'дроссель',
            'карбюратор',
            'свіча',
            'свеча',
            'катушка запалювання',
            'катушка зажигания',
            'катушк',
            'маховик',
            'клапан',
            'колектор впускн',
            'коллектор впускной',
            'гідрокомпенсатор',
            'гидрокомпенсатор',
            'маслознімн',
            'маслосъемн',
            'маслян',
            'масл',
            'насос масл',
            'педаль',
            'компресор',
            'компрессор',
            'помпа',
            'насос',
            'прокладка голівки',
            'гбц',
            'башка',
            'вкладиш',
            'вкладыш',
            'гільз',
            'гильз',
            'грм',
            'колен',
            'двигло',
            'егр',
            'egr',
        ],
        'parts-transmission': [
            'коробк',
            'кпп',
            'акпп',
            'мкпп',
            'варіатор',
            'вариатор',
            'шрус',
            'піввісь',
            'полуось',
            'кардан',
            'зчеплення',
            'зчепленн',
            'сцепление',
            'сцеплени',
            'кошик зчеплення',
            'корзина сцепления',
            'диск зчеплення',
            'диск сцепления',
            'вилк',
            'підшипник',
            'підшипник',
            'шестерня',
            'шестерн',
            'синхронізатор',
            'синхронизатор',
            'роздатк',
            'раздатк',
            'хаб',
            'редуктор мост',
            'черв',
            'привід',
            'привод',
            'трос',
            'вал',
            'гранат',
            'корзин',
            'кошик',
            'вижимн',
            'выжимн',
            'дсг',
            'dsg',
            'мост',
            'хрестовин',
            'крестовин',
        ],
        'parts-body': [
            'бампер',
            'посилений бампер',
            'усилитель бампера',
            'капот',
            'крило',
            'крыло',
            'двер',
            'порог',
            'петл',
            'молдинг',
            'рейлінг',
            'рейлинг',
            'спойлер',
            'обшивк',
            'рамк',
            'кришк',
            'крышк',
            'кришка багажника',
            'крышка багажника',
            'люк',
            'кабель',
            'решетк',
            'бризговик',
            'брызговик',
            'підкрылок',
            'подкрылок',
            'кузов',
            'арка',
            'заслінк',
            'заслонка',
            'брудозбірник',
            'грязезащит',
            'накладк',
            'усилитель',
            'підсилювач',
            'косинк',
            'косынк',
            'захист',
            'защит',
            'кріплення',
            'крепление',
            'болт',
            'гайк',
            'гайка',
            'хомут',
            'шайб',
            'шпильк',
            'фурнітур',
            'підніжк',
            'облицювання',
            'лонжерон',
            'кенгурятник',
            'кенгурин',
        ],
        'parts-optics': [
            'фара',
            'противотуманк',
            'протитуманк',
            'птф',
            'фонар',
            'ліхтар',
            'лихтар',
            'повторювач',
            'повторитель',
            'відбивач',
            'отражатель',
            'оптика',
            'лампа',
            'лінза',
            'линза',
            'стоп-сигнал',
            'стоп сигнал',
            'габарит',
            'поворотник',
            'поворотник',
            'рефлектор',
            'задній ліхтар',
            'блок фар',
            'блок фа',
            'мігачок',
            'мигачок',
            'поворотець',
            'туманк',
            'фары',
            'фари',
            'стопи',
            'стопы',
        ],
        'parts-glass': [
            'скло',
            'стекло',
            'лобов',
            'лобовое',
            'вітрове',
            'ветровое',
            'дзеркал',
            'зеркал',
            'триплекс',
            'склоочисник скла',
            'бокове скло',
            'боковое стекло',
            'лобовуха',
            'лобовик',
        ],
        'parts-suspension': [
            'амортизатор',
            'пружин',
            'важіль',
            'важел',
            'рычаг',
            'стійк',
            'стойк',
            'ступиц',
            'кульов',
            'шаров',
            'сайлентблок',
            'кронштейн',
            'поворотний кулак',
            'поворотний кулак',
            'балка',
            'підрамник',
            'подрамник',
            'тяга',
            'наконечник',
            'стабілізатор',
            'стабилизатор',
            'пильник',
            'пыльник',
            'відбійник',
            'отбойник',
            'опора стійк',
            'опора стойк',
            'опора',
            'шарнір',
            'шарнир',
            'підвіск',
            'подвеск',
            'ходова',
            'ходовк',
            'ходов',
            'лінк',
            'линк',
            'резинк',
        ],
        'parts-brakes': [
            'гальм',
            'тормоз',
            'гальмівний диск',
            'тормозной диск',
            'диск гальм',
            'супорт',
            'суппорт',
            'колодк',
            'барабан',
            'гальмівний циліндр',
            'тормозной цилиндр',
            'циліндр',
            'цилиндр',
            'гтц',
            'вакуумник',
            'вакуумний підсилювач',
            'вакуумный усилитель',
            'скоба',
            'шланг',
            'абс',
            'abs',
        ],
        'parts-steering': [
            'кермо',
            'руль',
            'рейк',
            'рульова',
            'рулевая',
            'кермувальн',
            'реечн',
            'гур',
            'еур',
            'эур',
            'гідропідсилювач керма',
            'гидроусилитель руля',
            'насос гур',
            'насос гідропідсилювача',
            'кардан рульов',
            'кардан рулев',
            'тяга рульов',
            'наконечник рульов',
            'рульова колонка',
            'рульов колонк',
            'підсилювач керма',
            'гидрач',
            'гідрач',
            'маятник',
        ],
        'parts-electrics': [
            'проводк',
            'провід',
            'джгут',
            'жгут',
            'перемикач',
            'переключатель',
            'кнопк',
            'модуль',
            'запобіжник',
            'предохранитель',
            'контролер',
            'контроллер',
            'ecu',
            'ебу',
            'блок управл',
            'розєм',
            'разъем',
            'колодка провод',
            'діод',
            'диод',
            'резистор',
            'транзистор',
            'статор',
            'ротор',
            'соленоїд',
            'соленоид',
            'склопідйомник',
            'стеклоподъемник',
            'моторчик',
            'звуков',
            'сирен',
            'антенн',
            'камер',
            'парктронік',
            'парктроник',
            'акумулятор',
            'аккумулятор',
            'акб',
            'мозги',
            'реле',
            'обігрів',
            'обогрев',
            'лампочк',
            'диммер',
            'світлодіод',
            'led',
            'склоочисник',
            'коса',
            'бендикс',
            'втягивающ',
            'втягуюч',
            'запалювання',
            'зажигание',
        ],
        'parts-cooling': [
            'радіатор',
            'радиатор',
            'радіатор пічки',
            'радиатор печки',
            'радіатор кондиціонер',
            'радиатор кондиционер',
            'вентилятор',
            'термостат',
            'патрубк',
            'бачок',
            'охолоджен',
            'охлажден',
            'трубк',
            'конденсор',
            'ресивер',
            'осушувач',
            'осушитель',
            'помп',
            'антифриз',
            'печк',
            'пічк',
            'кондиціонер',
            'кондиционер',
            'компресор кондиц',
            'компрессор кондиц',
            'випарник',
            'испаритель',
            'повітропровід',
            'воздуховод',
            'дефлектор',
            'інтеркулер',
            'интеркулер',
            'вискомуфт',
        ],
        'parts-fuel': [
            'паливн',
            'топливн',
            'бак',
            'бензобак',
            'бензонасос',
            'паливний насос',
            'топливный насос',
            'форсунк',
            'інжектор',
            'инжектор',
            'адсорбер',
            'evap',
            'горловин',
            'тнвд',
            'гбо',
            'газобалон',
            'газобалонн',
            'редуктор гбо',
            'мультиклапан',
            'балон гбо',
            'рамп',
        ],
        'parts-exhaust': [
            'глушник',
            'глушитель',
            'катализатор',
            'каталізатор',
            'резонатор',
            'вихлопн',
            'выпускн',
            'гофр',
            'приймач',
            'колектор випускн',
            'вихлоп',
            'выхлоп',
            'банк',
            'паук',
            'пламегаситель',
        ],
        'parts-interior': [
            'сидінн',
            'сиденье',
            'крісл',
            'спинк',
            'торпед',
            'приборн',
            'консоль',
            'килимок салону',
            'ковер',
            'потолок',
            'підлокітник',
            'подлокотник',
            'підголовник',
            'подголовник',
            'подушка безпек',
            'подушка безопас',
            'ейрбег',
            'airbag',
            'ремень безпек',
            'ремен безпек',
            'ремінь безпек',
            'ремень безопас',
            'чохол',
            'чехол',
            'попільничк',
            'пепельница',
            'вещев',
            'бардачок',
            'карта двер',
            'підлогов',
            'обшивк салон',
            'рукоятк',
            'ворс',
            'шторк',
            'шторк',
            'бород',
        ],
        'parts-filters': [
            'паливний фільтр',
            'топливный фильтр',
            'масляний фільтр',
            'масляный фильтр',
            'повітряний фільтр',
            'воздушный фильтр',
            'салонний фільтр',
            'салонный фильтр',
            'фільтр',
            'фильтр',
            'фільтрувальн',
        ],
        'parts-sensors': [
            'датчик',
            'лямбда',
            'зонд',
            'сенсор',
            'імпульс',
            'давлення',
            'давления',
            'temperature',
            'temp',
            'детонац',
            'положення колінвал',
            'положения коленвал',
            'abs датчик',
        ],
        'parts-locks': [
            'замок',
            'замк',
            'личинк',
            'засувк',
            'фіксатор',
            'фиксатор',
            'брелок',
            'сигналізац',
            'сигнализаци',
            'іммобілайзер',
            'иммобилайзер',
            'ключ',
        ],
        'parts-wipers': [
            'щітк',
            'щетк',
            'дворник',
            'склоочисник',
            'очисник скла',
            'омивач',
            'омывател',
            'бачок омив',
            'насос омив',
            'трапеція',
            'трапеция',
            'фароочисник',
        ],
    },
    'parts-wheels': {
        'parts-tires': [
            'шин',
            'шина',
            'покришк',
            'покрышк',
            'резин',
            'зимов',
            'літн',
            'всесезон',
            'всесезонн',
        ],
        'parts-rims': [
            'диск',
            'диски',
            'ковпак',
            'обід',
            'обод',
            'колісн диск',
            'колесн диск',
            'литі',
            'литье',
            'литые',
            'ковка',
            'ковані',
            'кованые',
            'штамповка',
        ],
        'parts-wheelsets': ['в зборі', 'колесо в зборі', 'колеса в зборі', 'комплект колес'],
    },
    'parts-accessories': {
        'parts-audio': [
            'магнітол',
            'магнитол',
            'радіо',
            'радио',
            'динамік',
            'динамик',
            'колонк',
            'сабвуфер',
            'аудіо',
            'аудио',
            'відеореєстратор',
            'видеорегистратор',
            'реєстратор',
            'регистратор',
            'навігатор',
            'навигатор',
            'головний пристрій',
        ],
        'parts-chem': [
            'масло',
            'масла',
            'олія',
            'мастило',
            'смазк',
            'автохімія',
            'автохімія',
            'хімія',
            'химия',
            'поліроль',
            'полироль',
            'очищувач',
            'очиститель',
            'тосол',
            'антифриз',
            'незамерзайк',
            'омивач',
            'адгезив',
            'герметик',
        ],
        'parts-tuning': [
            'тюнінг',
            'тюнинг',
            'обвіс',
            'обвес',
            'дефлектор',
            'фаркоп',
            'багажник',
            'коврик',
            'коврики',
            'килимк',
            'спойлер',
            'боді-кіт',
            'боди-кит',
            'тонувальн',
            'тонировочн',
        ],
    },
    'parts-trucks': {
        'parts-trucks-cabin': ['кабін', 'кабин', 'спальн', 'підйомник кабіни', 'подъемник кабины'],
        'parts-trucks-hydraulics': [
            'гідроциліндр',
            'гидроцилиндр',
            'гідроборт',
            'гидроборт',
            'гідравл',
            'гидравл',
            'маніпулятор',
            'манипулятор',
            'кран',
            'насос гідравл',
            'масляний бак',
            'масляный бак',
        ],
        'parts-trucks-chassis': [
            'рама',
            'мост',
            'рессор',
            'ресор',
            'балка',
            'шасі',
            'шасси',
            'ходов',
            'підвіск',
            'подвеск',
            'стійк',
            'стойк',
            'ступиц',
            'тележк',
            'тележк',
            'седельно',
            'седельн',
        ],
        'parts-trucks-engine': [
            'двигун',
            'двигатель',
            'мотор',
            'турбин',
            'інтеркулер',
            'интеркулер',
            'радіатор',
            'радиатор',
            'помп',
            'паливн',
            'топливн',
            'зчеплення',
            'сцепление',
            'коробк',
            'кпп',
        ],
    },
};
/** Нормалізовані ключі будуються один раз при першому використанні карти. */
const normalizedCache = new Map();
function flatKeywords(scopeId) {
    let flat = normalizedCache.get(scopeId);
    if (!flat) {
        flat = [];
        const map = scopedKeywords[scopeId] ?? {};
        for (const [id, kws] of Object.entries(map)) {
            for (const kw of kws) {
                flat.push({ kw: normalize(kw), id });
            }
        }
        normalizedCache.set(scopeId, flat);
    }
    return flat;
}
/**
 * Класифікує текст у підкатегорію розділу parentCategoryId.
 * Виграє підкатегорія з найдовшим ключовим словом, що зустрілось
 * в тексті; без совпадінь — сам parentCategoryId.
 */
export function classifyScoped(text, parentCategoryId) {
    const parent = findCategoryById(parentCategoryId);
    if (!parent?.subcategories?.length)
        return parentCategoryId;
    const normalized = normalize(text);
    if (!normalized)
        return parentCategoryId;
    let bestId = null;
    let bestLen = 0;
    for (const { kw, id } of flatKeywords(parent.id)) {
        if (kw.length > bestLen && normalized.includes(kw)) {
            bestId = id;
            bestLen = kw.length;
        }
    }
    return bestId ?? parentCategoryId;
}
/**
 * Найдовше ключове слово будь-якого скопу, що міститься у нормалізованому
 * тексті, — для розбору пошукового запиту розумним пошуком (група запчастини
 * прямо з вільного тексту типу «двигатель грейт вол ховер 2,4»).
 */
export function matchScopedKeywordAllScopes(text) {
    let best = null;
    for (const scopeId of Object.keys(scopedKeywords)) {
        for (const { kw, id } of flatKeywords(scopeId)) {
            if (kw.length > (best?.kw.length ?? 0) && text.includes(kw)) {
                best = { kw, id };
            }
        }
    }
    return best;
}
//# sourceMappingURL=parts-classifier.js.map